import Matter from "matter-js";
import { PointerEvent, useCallback, useEffect, useRef, useState } from "react";
import { m } from "framer-motion";
import { EASE } from "./Motion";
import { LogeMember } from "../../types/entities";

// Real physics, not a canned animation: the coin is a Matter.js body that
// falls under gravity and bounces off pin bodies exactly like a Pachinko
// machine, so the outcome comes from actual chaotic collisions (the player's
// chosen entry lane, plus tiny differences that compound on every pin hit)
// rather than from a scripted random pick dressed up visually.

const PIN_RADIUS = 3;
// Big enough that the DSS logo printed on the coin is actually legible mid-
// fall, not just in the winner banner. PIN_SPACING_X and PIN_WALL_MARGIN
// below are derived from this rather than hardcoded, so bumping it can't
// silently reintroduce the coin-gets-wedged bug a smaller, undersized gap
// caused before.
const COIN_RADIUS = 16;
const WALL_THICKNESS = 20;
// Target centre-to-centre pin spacing (buildBoard stretches it a little to
// divide the board into whole columns), picked so the gap between two pins
// in the *same* row — the tightest regular gap in the lattice, and the one that
// also governs how close a pin can sit to a side wall before it pinches the
// coin against it — comfortably clears the coin's diameter (a margin of a
// few px, not a few tenths: too tight and the coin can wedge to a dead stop
// against two pins, or a pin and a wall, and never move again).
const PIN_SPACING_X = COIN_RADIUS * 2 + PIN_RADIUS * 2 + 5;
const PIN_ROW_SPACING = Math.round(PIN_SPACING_X * 0.86);
// How far the lattice's outermost pin column sits from each side wall. A wide
// margin (a coin's diameter or more) looks like the safe choice but isn't:
// it opens a pin-free lane along the wall exactly the coin's width, letting
// it fall straight down the edge untouched instead of bouncing through the
// field at all — which is what "pins are missing to the sides" looks like
// in play. Kept tight instead, close to the coin's own radius, so a coin
// hugging the wall still meets a pin on every even row. The trade-off is
// that a pin sitting this close to a flat, immovable wall can occasionally
// pinch the coin into a real (if short-lived) stall; STUCK_FRAMES below is
// the backstop that recovers from that, so this can stay tight rather than
// being loosened until the stall never happens (which, per the above, also
// means it stops being a real Pachinko field near the edges).
const PIN_WALL_MARGIN = COIN_RADIUS + PIN_RADIUS;
// Fractions of the canvas height: a launch rail at the top the player taps
// to choose an entry lane, a dense pin field, and a short funnel at the
// bottom where dividers channel the coin into its final bin.
const RAIL_FRACTION = 0.09;
// The launch rail spans a narrow strip in the middle of the board rather
// than its full width. A Pachinko coin's sideways spread grows only with the
// square root of the pin rows it crosses, which over this board's ~13 rows
// comes out at roughly two bins either side of where it entered — so from a
// full-width rail a coin released above one end simply cannot reach the bin
// at the far end. Simulating the real engine put that at ~1% against a 14%
// fair share for seven members, i.e. an unwinnable member for that drop.
// Entering from the middle keeps every member genuinely winnable from any
// tap: measured over the same simulation, the worst tap in this zone still
// leaves the least likely bin near 10%, where a 24-30% zone drops it to 5%.
const DROP_ZONE_FRACTION = 0.18;
// ...but never so narrow that the strip stops being a comfortable tap target.
const DROP_ZONE_MIN_WIDTH = 56;
const PIN_AREA_FRACTION = 0.86;
const SETTLE_SPEED = 0.05;
const SETTLE_FRAMES = 30;
// How many consecutive near-stationary frames mid-air (not settling in a
// bin) before the coin gets an unstick kick — see the stuck-detection
// comment in tick() below.
const STUCK_FRAMES = 18;
// Rebuilding the whole static world resets any coin in flight, so a resize
// only triggers it once the size has actually moved by more than this many
// CSS pixels — sub-pixel/no-op ResizeObserver callbacks (safe-area
// recalculation, web-font swap reflow, framer-motion layout ticks) are
// otherwise indistinguishable from a real resize and would otherwise wipe a
// coin out of the air mid-drop.
const RESIZE_EPSILON = 1;

type Phase = "idle" | "dropping" | "settled";

interface Layout {
  width: number;
  height: number;
  numBins: number;
  binWidth: number;
  railBottom: number;
  zoneLeft: number;
  zoneRight: number;
  pinAreaBottom: number;
  floorY: number;
}

let coinImage: HTMLImageElement | null = null;
function getCoinImage(): HTMLImageElement {
  if (!coinImage) {
    coinImage = new Image();
    coinImage.src = "/icon-512.png";
  }
  return coinImage;
}

export default function GaltonBoard({
  members,
  onSettled,
}: {
  members: Pick<LogeMember, "id" | "name" | "emoji">[];
  onSettled?: (member: Pick<LogeMember, "id" | "name" | "emoji">) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const engineRef = useRef<Matter.Engine | null>(null);
  const coinBodyRef = useRef<Matter.Body | null>(null);
  const layoutRef = useRef<Layout | null>(null);
  const rafRef = useRef<number | null>(null);
  const settleCounterRef = useRef(0);
  const stuckCounterRef = useRef(0);
  // Counts consecutive unstick kicks within the current drop, so a coin
  // wedged hard against a wall gets progressively harder shoves instead of
  // the same one repeating — see the escalation comment where it's used.
  const unstickKicksRef = useRef(0);
  const phaseRef = useRef<Phase>("idle");
  // The x the next coin drops from — chosen by tapping the launch rail.
  // Defaults to centre once the board has a size.
  const launchXRef = useRef<number | null>(null);

  const [phase, setPhase] = useState<Phase>("idle");
  const [winnerIndex, setWinnerIndex] = useState<number | null>(null);
  const [hasDropped, setHasDropped] = useState(false);

  const numBins = Math.max(members.length, 1);

  // Refs so the one long-lived render loop always sees the latest roster
  // and callback without needing to be torn down and restarted for it.
  const membersRef = useRef(members);
  membersRef.current = members;
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;

  // (Re)builds the static board — walls, pin field, bin dividers — for the
  // current container size and member count.
  const buildBoard = useCallback(
    (width: number, height: number) => {
      const engine = Matter.Engine.create({ gravity: { x: 0, y: 1 } });
      engineRef.current = engine;

      const railBottom = height * RAIL_FRACTION;
      const pinAreaBottom = height * PIN_AREA_FRACTION;
      const floorY = height - 8;
      const binWidth = width / numBins;

      const zoneWidth = Math.min(
        width - (COIN_RADIUS + 2) * 2,
        Math.max(DROP_ZONE_MIN_WIDTH, width * DROP_ZONE_FRACTION),
      );
      const zoneLeft = (width - zoneWidth) / 2;
      const zoneRight = zoneLeft + zoneWidth;

      const layout: Layout = {
        width,
        height,
        numBins,
        binWidth,
        railBottom,
        zoneLeft,
        zoneRight,
        pinAreaBottom,
        floorY,
      };
      layoutRef.current = layout;
      if (launchXRef.current === null) launchXRef.current = width / 2;

      const statics: Matter.Body[] = [];

      // Side walls, floor.
      statics.push(
        Matter.Bodies.rectangle(-WALL_THICKNESS / 2, height / 2, WALL_THICKNESS, height * 2, {
          isStatic: true,
        }),
        Matter.Bodies.rectangle(width + WALL_THICKNESS / 2, height / 2, WALL_THICKNESS, height * 2, {
          isStatic: true,
        }),
        Matter.Bodies.rectangle(width / 2, floorY + WALL_THICKNESS / 2, width * 2, WALL_THICKNESS, {
          isStatic: true,
          friction: 0.9,
        }),
      );

      // Dense Pachinko pin field: a brick lattice spanning the full width at
      // a fixed, tight spacing (independent of the bin count) so the coin
      // takes many chaotic hits on the way down, not just one per bin.
      const pinAreaTop = railBottom;
      const pinRows = Math.max(6, Math.floor((pinAreaBottom - pinAreaTop) / PIN_ROW_SPACING));
      // Spreads the rows evenly across the available height rather than
      // using PIN_ROW_SPACING verbatim, so a short board still fills its
      // full height with pins — this can only *increase* the spacing versus
      // the constant (rows round down), so the coin-clearance margin the
      // constants were picked for never shrinks.
      const actualRowSpacing = (pinAreaBottom - pinAreaTop) / (pinRows + 1);

      // Rather than stepping a fixed spacing out from the left wall — which
      // leaves whatever phase the arithmetic happens to end on at the right
      // wall, so the right edge's behaviour depended on the board's width —
      // divide the field into a whole number of columns and let the spacing
      // flex slightly to fit. Both edges are then identical by construction,
      // at any width:
      //
      //   even rows — a pin on each wall, so the coin can never free-fall
      //               down an edge untouched
      //   odd rows  — pins on the exact midpoints, so both edge lanes stand
      //               open and the end bins stay reachable
      //
      // Both halves matter. Pinning the wall on *every* row (which an earlier
      // attempt at closing the free-fall channel did) is a solid vertical
      // wall, and walls the end bins off almost entirely.
      const fieldLeft = PIN_WALL_MARGIN;
      const fieldRight = width - PIN_WALL_MARGIN;
      const fieldWidth = fieldRight - fieldLeft;
      // Start from the spacing the constants ask for, then widen it if
      // rounding to whole columns would squeeze a row's gaps below what the
      // coin needs to drop through — a row it cannot pass at all would stop
      // the drop dead.
      let pinCols = Math.max(2, Math.round(fieldWidth / PIN_SPACING_X));
      while (pinCols > 2 && fieldWidth / pinCols - PIN_RADIUS * 2 < COIN_RADIUS * 2 + 3) {
        pinCols -= 1;
      }
      const colSpacing = fieldWidth / pinCols;

      const pin = (x: number, y: number) =>
        Matter.Bodies.circle(x, y, PIN_RADIUS, {
          isStatic: true,
          restitution: 0.55,
          friction: 0.05,
        });
      for (let row = 0; row < pinRows; row++) {
        const y = pinAreaTop + (row + 1) * actualRowSpacing;
        const wallRow = row % 2 === 0;
        const count = wallRow ? pinCols + 1 : pinCols;
        const rowStart = wallRow ? fieldLeft : fieldLeft + colSpacing / 2;
        for (let i = 0; i < count; i++) {
          statics.push(pin(rowStart + i * colSpacing, y));
        }
      }

      // Bin dividers: a short funnel just above the floor — low walls, so
      // the coin spends almost the whole drop bouncing through pins rather
      // than sliding down a tall chute.
      for (let i = 1; i < numBins; i++) {
        const x = i * binWidth;
        const dividerHeight = floorY - pinAreaBottom;
        statics.push(
          Matter.Bodies.rectangle(x, pinAreaBottom + dividerHeight / 2, 3, dividerHeight, {
            isStatic: true,
            friction: 0.05,
          }),
        );
      }

      Matter.Composite.add(engine.world, statics);
    },
    [numBins],
  );

  const drawCoinAt = useCallback(
    (ctx: CanvasRenderingContext2D, x: number, y: number, angle: number) => {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(angle);
      ctx.shadowColor = "oklch(0.14 0.014 75 / 0.35)";
      ctx.shadowBlur = 8;
      ctx.shadowOffsetY = 3;
      ctx.beginPath();
      ctx.arc(0, 0, COIN_RADIUS, 0, Math.PI * 2);
      ctx.closePath();
      ctx.fillStyle = "oklch(0.965 0.008 85)";
      ctx.fill();
      ctx.shadowColor = "transparent";
      ctx.clip();
      const img = getCoinImage();
      if (img.complete && img.naturalWidth > 0) {
        ctx.drawImage(img, -COIN_RADIUS, -COIN_RADIUS, COIN_RADIUS * 2, COIN_RADIUS * 2);
      }
      ctx.restore();
      ctx.beginPath();
      ctx.arc(x, y, COIN_RADIUS, 0, Math.PI * 2);
      ctx.strokeStyle = "oklch(0.14 0.014 75 / 0.55)";
      ctx.lineWidth = 0.5;
      ctx.stroke();
    },
    [],
  );

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const layout = layoutRef.current;
    const engine = engineRef.current;
    if (!canvas || !layout || !engine) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    ctx.save();
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, layout.width, layout.height);

    // Launch rail guide.
    ctx.strokeStyle = "oklch(0.66 0.010 90)";
    ctx.setLineDash([3, 4]);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(layout.zoneLeft, layout.railBottom);
    ctx.lineTo(layout.zoneRight, layout.railBottom);
    ctx.stroke();
    ctx.setLineDash([]);

    // "DROP ZONE" label, drawn faint and early so it reads as a watermark
    // on the rail rather than a UI chrome element — the coin (drawn last,
    // below) sits visually on top of it, including the resting preview coin
    // that shares this same strip.
    ctx.save();
    ctx.font = "600 8px ui-monospace, SFMono-Regular, Menlo, monospace";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "oklch(0.66 0.010 90 / 0.45)";
    if ("letterSpacing" in ctx) {
      (ctx as CanvasRenderingContext2D & { letterSpacing: string }).letterSpacing = "1px";
    }
    ctx.fillText("DROP ZONE", layout.width / 2, layout.railBottom / 2);
    ctx.restore();

    // Bin dividers + floor.
    ctx.strokeStyle = "oklch(0.66 0.010 90)";
    ctx.lineWidth = 1;
    for (let i = 1; i < layout.numBins; i++) {
      const x = i * layout.binWidth;
      ctx.beginPath();
      ctx.moveTo(x, layout.pinAreaBottom);
      ctx.lineTo(x, layout.floorY);
      ctx.stroke();
    }
    ctx.beginPath();
    ctx.moveTo(0, layout.floorY);
    ctx.lineTo(layout.width, layout.floorY);
    ctx.stroke();

    // Pins.
    ctx.fillStyle = "oklch(0.54 0.012 75)";
    const bodies = Matter.Composite.allBodies(engine.world);
    for (const body of bodies) {
      if (!body.isStatic || body.label !== "Circle Body") continue;
      ctx.beginPath();
      ctx.arc(body.position.x, body.position.y, PIN_RADIUS, 0, Math.PI * 2);
      ctx.fill();
    }

    // Coin: the live physics body while one exists, otherwise a resting
    // preview at the currently chosen launch position so the player always
    // sees where the next drop will start.
    const coin = coinBodyRef.current;
    if (coin) {
      drawCoinAt(ctx, coin.position.x, coin.position.y, coin.angle);
    } else if (launchXRef.current !== null) {
      drawCoinAt(ctx, launchXRef.current, layout.railBottom / 2, 0);
    }

    ctx.restore();
  }, [drawCoinAt]);

  // Runs every frame for the lifetime of the component. It must always
  // reschedule itself — bailing out early without calling
  // requestAnimationFrame again (as an earlier version did, both here and
  // in the settle branch below) permanently kills the loop the first time
  // it runs before the board exists yet, which is normally the very first
  // frame: canvas.getContext + draw() would then only ever fire once, from
  // the resize observer's own direct call, leaving whatever it drew (pins,
  // an idle coin) on screen forever with no further updates — including no
  // physics update for a coin dropped afterwards, which is why the coin
  // used to appear to vanish/freeze the moment it was released.
  const tick = useCallback(() => {
    const engine = engineRef.current;
    const layout = layoutRef.current;

    if (engine && layout) {
      const coin = coinBodyRef.current;
      // A coin released exactly on a lattice line can land dead-centred in
      // the V between two pins with the two contact impulses cancelling
      // exactly — a real (if rare) symmetric equilibrium, not a bug, but one
      // a deterministic solver has no numerical noise to ever break out of
      // on its own. A tiny random horizontal nudge every frame it's still
      // among the pins — standing in for the air currents and surface
      // imperfections a real pinball has — guarantees it never actually
      // stops moving until it clears the pin field. Confined to the pin
      // field (rather than applied for the whole drop) so it can't also
      // disturb the coin once it's settling into its bin, which the speed
      // check just below depends on being genuinely still.
      if (phaseRef.current === "dropping" && coin && coin.position.y < layout.pinAreaBottom) {
        Matter.Body.applyForce(coin, coin.position, {
          x: (Math.random() - 0.5) * coin.mass * 0.0004,
          y: 0,
        });
      }

      Matter.Engine.update(engine, 1000 / 60);
      draw();

      if (phaseRef.current === "dropping" && coin) {
        const speed = Matter.Vector.magnitude(coin.velocity);
        const nearFloor = coin.position.y > layout.floorY - COIN_RADIUS - 4;

        // Belt-and-braces for the same equilibrium problem the jitter above
        // handles: a coin pinned between a pin and the (perfectly flat,
        // infinitely rigid) side wall can settle into a stall the jitter's
        // tiny force isn't always enough to break within a reasonable time.
        // Rather than chase an exact pin-spacing/wall-margin number that
        // guarantees it never happens — any such number trades off against
        // how far the coin can drift untouched along the wall before
        // hitting a pin at all — detect a genuine mid-air stall (stopped,
        // but nowhere near its bin) and give it a real corrective kick away
        // from whichever wall it's closest to.
        if (!nearFloor && speed < SETTLE_SPEED) {
          stuckCounterRef.current += 1;
        } else {
          stuckCounterRef.current = 0;
        }
        if (stuckCounterRef.current > STUCK_FRAMES) {
          const towardCentre = coin.position.x < layout.width / 2 ? 1 : -1;
          // Escalates on repeat stalls: a coin wedged hard enough into a
          // corner (pin + wall on one side, another pin on the other) can
          // eat the same kick and stall again a few frames later. Rather
          // than the player watching it twitch in place, each consecutive
          // kick within this drop hits harder than the last.
          unstickKicksRef.current += 1;
          const power = Math.min(unstickKicksRef.current, 4);
          Matter.Body.setVelocity(coin, {
            x: towardCentre * (5 + Math.random() * 2) * power,
            y: 3 * power,
          });
          stuckCounterRef.current = 0;
        }

        if (speed < SETTLE_SPEED && nearFloor) {
          settleCounterRef.current += 1;
        } else {
          settleCounterRef.current = 0;
        }
        if (settleCounterRef.current > SETTLE_FRAMES) {
          const index = Math.min(
            layout.numBins - 1,
            Math.max(0, Math.floor(coin.position.x / layout.binWidth)),
          );
          phaseRef.current = "settled";
          setPhase("settled");
          setWinnerIndex(index);
          const winner = membersRef.current[index];
          if (winner) onSettledRef.current?.(winner);
        }
      }
    }

    rafRef.current = requestAnimationFrame(tick);
  }, [draw]);

  // Build (and rebuild on a genuine container resize) the static board.
  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      if (width < 10 || height < 10) return;

      const prev = layoutRef.current;
      if (
        prev &&
        Math.abs(prev.width - width) < RESIZE_EPSILON &&
        Math.abs(prev.height - height) < RESIZE_EPSILON
      ) {
        return;
      }
      // A coin mid-drop keeps flying through the old board rather than
      // being wiped by an incidental layout shift — the rebuild (and the
      // reset to idle it implies) is deferred until it settles.
      if (phaseRef.current === "dropping") return;

      const dpr = window.devicePixelRatio || 1;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      if (engineRef.current) Matter.Engine.clear(engineRef.current);
      coinBodyRef.current = null;
      phaseRef.current = "idle";
      settleCounterRef.current = 0;
      stuckCounterRef.current = 0;
      unstickKicksRef.current = 0;
      setPhase("idle");
      setWinnerIndex(null);
      buildBoard(width, height);
      draw();
    });
    observer.observe(container);

    return () => {
      observer.disconnect();
      if (engineRef.current) Matter.Engine.clear(engineRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildBoard]);

  // The one long-lived render loop, started on mount and never restarted —
  // `tick` reads the latest board/members/callback through refs, so a
  // resize or roster change doesn't need to tear this down.
  useEffect(() => {
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [tick]);

  const dropAt = useCallback((x: number) => {
    const engine = engineRef.current;
    const layout = layoutRef.current;
    if (!engine || !layout || phaseRef.current === "dropping") return;

    if (coinBodyRef.current) {
      Matter.Composite.remove(engine.world, coinBodyRef.current);
    }

    const clampedX = Math.min(layout.zoneRight, Math.max(layout.zoneLeft, x));
    launchXRef.current = clampedX;

    const coin = Matter.Bodies.circle(clampedX, -COIN_RADIUS * 2, COIN_RADIUS, {
      restitution: 0.5,
      friction: 0.02,
      frictionAir: 0.0025,
      density: 0.004,
    });
    // A stray touch of spin is the only extra randomness injected directly —
    // everything past the first pin comes purely from the physics
    // simulation reacting to the player's chosen lane.
    Matter.Body.setAngularVelocity(coin, (Math.random() - 0.5) * 0.3);
    Matter.Composite.add(engine.world, coin);
    coinBodyRef.current = coin;

    settleCounterRef.current = 0;
    stuckCounterRef.current = 0;
    unstickKicksRef.current = 0;
    phaseRef.current = "dropping";
    setPhase("dropping");
    setWinnerIndex(null);
    setHasDropped(true);
  }, []);

  const handleRailTap = (e: PointerEvent<HTMLDivElement>) => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    dropAt(e.clientX - rect.left);
  };

  return (
    <div className="flex flex-col h-full">
      <div ref={containerRef} className="relative flex-1 min-h-0 bg-card overflow-hidden">
        <canvas ref={canvasRef} className="absolute inset-0" />

        {/* The launch rail: tap along this strip to choose the coin's entry
            lane and release it from there — the core Pachinko interaction.
            Deliberately only a narrow strip in the middle of the board; see
            DROP_ZONE_FRACTION for why it isn't the full width. */}
        <div
          onPointerDown={handleRailTap}
          role="button"
          aria-label="Vælg startposition og kast mønten"
          className="absolute top-0 left-1/2 -translate-x-1/2 cursor-pointer"
          style={{
            height: `${RAIL_FRACTION * 100}%`,
            // Mirrors the zoneWidth clamp in buildBoard.
            width: `clamp(${DROP_ZONE_MIN_WIDTH}px, ${DROP_ZONE_FRACTION * 100}%, 100%)`,
          }}
        />

        {!hasDropped && phase === "idle" && (
          <m.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.4 }}
            className="absolute inset-x-0 top-1 flex justify-center pointer-events-none"
          >
            <span className="font-mono text-[9px] tracking-wide uppercase text-ink3 bg-paper/80 px-2 py-0.5 rounded-full">
              Tryk for at vælge start
            </span>
          </m.div>
        )}
      </div>

      <div className="flex mt-2 gap-1">
        {members.map((member, i) => {
          const isWinner = phase === "settled" && winnerIndex === i;
          return (
            <m.div
              key={member.id}
              animate={isWinner ? { scale: [1, 1.12, 1.05] } : { scale: 1 }}
              transition={{ duration: 0.45, ease: EASE }}
              className={`flex-1 min-w-0 flex flex-col items-center gap-0.5 py-2 rounded-lg border ${
                isWinner ? "bg-ink text-paper border-ink" : "border-line-soft text-ink3"
              }`}
            >
              <div className="text-lg leading-none">{member.emoji}</div>
              <div className="font-mono text-[8.5px] tracking-wide uppercase truncate max-w-full px-0.5">
                {member.name}
              </div>
            </m.div>
          );
        })}
      </div>

      <div className="mt-2 text-center font-mono text-[9.5px] tracking-wide uppercase text-ink3">
        {phase === "dropping"
          ? "Mønten ruller…"
          : phase === "settled"
            ? "Tryk øverst for at kaste igen"
            : "Tryk øverst på banen for at kaste mønten"}
      </div>
    </div>
  );
}
